import {
  useState,
  useMemo,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react';
import './styles.scss';
import { Reservation, Space, Table as TableType } from 'types/reservations';
import Card from 'Components/Molecules/Card';
import { noop, findOverlaps } from 'utils/helper';
import Table from './Components/Table';
import ShiftTimeline from './Components/ShiftTimeline';
import {
  ReservationActionTypes,
  ReservationContext,
} from 'Contexts/ReservationContext';
import { ReservationShift, ShiftType } from 'types/shifts';
import Modal from 'Components/Molecules/Modal';
import Checkbox from 'Components/Atoms/Checkbox';
import useLocalStorageState from 'CustomHooks/useLocalStorageState';
import { AuthContext } from 'Contexts/AuthContext';
import { RestaurantContext, Severity } from 'Contexts/RestaurantContext';
import usePinAuth from 'CustomHooks/usePinAuth';
import { SecurityGrant, trackChange } from 'utils/securityActivity/grant';
import useToast from 'CustomHooks/useToast';
import server from 'utils/server';
import {
  isFullDayTableBlock,
  tableBlockCoversWindow,
} from 'utils/tableBlocks';
import { toggleFullDayTableBlock } from 'utils/applyTableBlockChanges';

export type TimeTableProps = {
  reservations: Reservation[];
  handleReservationAction: (
    id: string,
    action: ReservationActionTypes,
    payload?: any,
  ) => void;
  handleReservationClick: (id: string) => void;
  handleTableClick: (id: string) => void;
  shifts: ReservationShift[];
  spaces: Space[];
  tables: TableType[];
  occassions: { id: string; title: string; hidden?: boolean }[];
  activeReservation: null | string;
  activeTable: null | string;
  loading: boolean;
  currentTime: number | null;
  overlayOn?: boolean;
};

export type TimeTable = {
  id: string;
  title: string;
  closed: boolean;
  // 0 = fully open, 1 = partly closed (some shifts close it), 2 = fully closed
  closedState: 0 | 1 | 2;
  start: number | null;
  close: number | null;
  tables: TableType[];
  reservations: Reservation[];
};

export type ReservabilityMap = { [tableName: string]: Set<number> };

// Mirrors the 3-state logic from SpaceSwitch's lock icon: how closed is this
// space relative to the currently active shifts?
//   0 = fully open (no active shift closes it)
//   1 = partly closed (at least one — but not all — active shifts close it)
//   2 = fully closed (every active, non-commentOnly shift closes it)
// A shift "closes" a space when any of: the shift itself is closed, the
// shift excludes this space (matchBoth), the shift overwrite lists the
// space as closed, or the shift blocks all reservations.
function getSpaceClosedState(
  shifts: ReservationShift[],
  spaceId: string,
): 0 | 1 | 2 {
  const openShifts = shifts.filter((s) => s.active && !s.commentOnly);
  if (!openShifts.length) return 0;

  const closedShifts = openShifts.filter(
    (s) =>
      s.closed ||
      (s.spaces?.length && !s.spaces.includes(spaceId) && s.matchBoth) ||
      (s.overwrite as any)?.closedSpaces?.includes(spaceId) ||
      !!s.overwrite?.blockReservations,
  );

  if (!closedShifts.length) return 0;
  if (closedShifts.length === openShifts.length) return 2;
  return 1;
}

function computeReservabilityMap(
  shifts: ReservationShift[],
  tables: TableType[],
  globalStart: number | null,
  globalClose: number | null,
): ReservabilityMap {
  const map: ReservabilityMap = {};
  if (globalStart == null || globalClose == null) return map;

  const activeShifts = shifts.filter(
    (s) =>
      s.active !== false &&
      !s.disabled &&
      !s.closed &&
      s.type === ShiftType.RESERVATION,
  );

  for (const table of tables) {
    const reservable = new Set<number>();

    for (const shift of activeShifts) {
      const spacesMatch =
        !shift.spaces?.length || shift.spaces.includes(table.space);
      const occassionsMatch =
        !shift.occassions?.length ||
        shift.occassions.some((o) => table.occassions.includes(o));

      const eligible = shift.matchBoth
        ? spacesMatch && occassionsMatch
        : spacesMatch || occassionsMatch;

      if (!eligible) continue;

      for (let b = shift.start; b < shift.close; b++) {
        reservable.add(b);
      }

      for (const blocked of shift.additional?.blockedAt ?? []) {
        reservable.delete(blocked);
      }
    }

    const notReservable = new Set<number>();
    for (let b = globalStart; b < globalClose; b++) {
      if (!reservable.has(b)) notReservable.add(b);
    }

    map[table.name] = notReservable;
  }

  return map;
}

const TimeTable = ({
  reservations = [],
  handleReservationAction = noop,
  handleTableClick = noop,
  handleReservationClick = noop,
  shifts = [],
  spaces = [],
  tables = [],
  occassions = [],
  currentTime = null,
  activeReservation = null,
  activeTable = null,
  loading = false,
  overlayOn = false,
}: TimeTableProps) => {
  // Shift IDs currently selected on the mini-timeline. Empty initial value
  // is treated as "still initializing" — the effect below syncs it to "all
  // shifts selected" once `shifts` is populated. When the effect later sees
  // new shifts arriving we merge them in (so switching dates doesn't deselect
  // newly-appeared shifts) but preserve any manual deselects on existing
  // ones.
  const [selectedShiftIds, setSelectedShiftIds] = useState<string[]>([]);
  const [touchedShiftIds, setTouchedShiftIds] = useState<string[]>([]);

  const shiftIdKey = useMemo(
    () =>
      shifts
        .map((s) => s.id)
        .filter(Boolean)
        .sort()
        .join('|'),
    [shifts],
  );

  useEffect(() => {
    const ids = shifts.map((s) => s.id).filter(Boolean) as string[];
    setSelectedShiftIds((prev) => {
      // First time we see shifts → select all.
      if (!prev.length && !touchedShiftIds.length) return ids;
      // Drop IDs that no longer exist; auto-select any new shift that the
      // user hasn't explicitly toggled yet.
      const existing = prev.filter((id) => ids.includes(id));
      const additions = ids.filter(
        (id) => !existing.includes(id) && !touchedShiftIds.includes(id),
      );
      return [...existing, ...additions];
    });
    // eslint-disable-next-line
  }, [shiftIdKey]);

  const onToggleShift = useCallback((shiftId: string) => {
    setTouchedShiftIds((prev) =>
      prev.includes(shiftId) ? prev : [...prev, shiftId],
    );
    setSelectedShiftIds((prev) =>
      prev.includes(shiftId)
        ? prev.filter((id) => id !== shiftId)
        : [...prev, shiftId],
    );
  }, []);

  const onSelectAllShifts = useCallback(() => {
    const ids = shifts.map((s) => s.id).filter(Boolean) as string[];
    setTouchedShiftIds([]);
    setSelectedShiftIds(ids);
  }, [shifts]);

  const [trackRect, settrackRect] = useState<DOMRect | null>(null);

  const measuredRef = useCallback((node) => {
    if (node !== null) {
      settrackRect(node.getBoundingClientRect());
    }
  }, []);

  // Window of the mini-timeline selection. "No selection" collapses the grid
  // to nothing — the timeline is still visible so the user can click a shift
  // to bring the day back.
  const selectedShiftRange: [number | null, number | null] = useMemo(() => {
    const filteredShifts = shifts.filter(
      (s) => !!s.id && selectedShiftIds.includes(s.id),
    );

    return filteredShifts.reduce(
      (acc: [number | null, number | null], cV) => {
        if (
          typeof cV.start === 'number' &&
          (acc[0] === null || acc[0] > cV.start)
        ) {
          acc[0] = cV.start;
        }
        if (
          typeof cV.close === 'number' &&
          (acc[1] === null || acc[1] < cV.close)
        ) {
          acc[1] = cV.close;
        }
        return acc;
      },
      [null, null],
    );
  }, [shifts, selectedShiftIds]);

  const timeTables: TimeTable[] = useMemo(() => {
    let [start, close] = selectedShiftRange;

    if (start === null || close === null) return [];

    const filteredTables = tables.sort(
      (a, b) =>
        parseInt(a.name) - parseInt(b.name) || a.name.localeCompare(b.name),
    );
    // .filter((t) =>
    //   filteredShifts.some(
    //     (s) =>
    //       s.spaces.includes(t.space) ||
    //       t.occassions.some((occ) => s.occassions.includes(occ)) ||

    //   )
    // );

    const spaceIds = filteredTables.reduce(
      (acc: string[], cV) => [...new Set([...acc, cV.space])],
      [],
    );

    const filteredReservations = reservations
      .filter(
        (r) =>
          r.status !== 'failed' &&
          (r.blocked
            ? tableBlockCoversWindow(r, start ?? 0, close ?? 0)
            : findOverlaps(r, start || 0, close || 0)) &&
          typeof r.startTimeInBlocks === 'number' &&
          typeof r.endTimeInBlocks === 'number',
      )
      // Full-day blocks read back as 96..208 (the after-midnight rule in
      // useReservations rewrites time "00:00"); clamp them to the visible
      // window so their bar spans the day instead of starting at 24:00.
      .map((r) =>
        r.blocked && isFullDayTableBlock(r)
          ? { ...r, startTimeInBlocks: start ?? 0, endTimeInBlocks: close ?? 0 }
          : r,
      );

    let newStart = Math.min(
      filteredReservations.sort(
        (a, b) => a.startTimeInBlocks - b.startTimeInBlocks,
      )?.[0]?.startTimeInBlocks || 1000,
      start,
    );

    let lastRes = filteredReservations.sort((a, b) => {
      let endA =
        a.endTimeInBlocks > a.startTimeInBlocks
          ? a.endTimeInBlocks
          : a.endTimeInBlocks + 96;

      let endB =
        a.endTimeInBlocks > a.startTimeInBlocks
          ? a.endTimeInBlocks
          : a.endTimeInBlocks + 96;

      return endB - endA;
    })?.[0];

    let newClose = close;

    if (lastRes && lastRes.endTimeInBlocks) {
      let endOfRes =
        lastRes.endTimeInBlocks > lastRes.startTimeInBlocks
          ? lastRes.endTimeInBlocks
          : lastRes.endTimeInBlocks + 96;

      if (endOfRes > newClose) {
        newClose = endOfRes;
      }
    }

    const tableIds = tables.map((t) => t.name);

    const arr: TimeTable[] = spaceIds.map((id) => {
      const closedState = getSpaceClosedState(shifts, id);
      return {
        id,
        title: spaces.find((s) => s.id === id)?.name || id,
        closed: closedState === 2,
        closedState,
        start: newStart,
        close: newClose,
        tables: tables
          .filter((t) => t.space === id)
          // Trim for the comparison only: table names can carry stray
          // whitespace (" 44") and reservations reference the stored name.
          .sort((a, b) =>
            a.name
              .trim()
              .localeCompare(b.name.trim(), undefined, { numeric: true }),
          ),
        reservations: filteredReservations.filter(
          (r) =>
            r.space === id || tableIds.some((t) => !!r.tables?.includes(t)),
        ),
      };
    });

    return arr;
  }, [spaces, tables, reservations, shifts, selectedShiftRange]);

  const reservabilityMap: ReservabilityMap = useMemo(() => {
    if (!timeTables.length) return {};

    const globalStart = timeTables.reduce<number | null>(
      (acc, tt) =>
        tt.start == null
          ? acc
          : acc == null
          ? tt.start
          : Math.min(acc, tt.start),
      null,
    );
    const globalClose = timeTables.reduce<number | null>(
      (acc, tt) =>
        tt.close == null
          ? acc
          : acc == null
          ? tt.close
          : Math.max(acc, tt.close),
      null,
    );

    // Availability reflects ONLY the currently selected shifts: cells
    // outside any selected shift's window (or belonging to tables none of
    // the selected shifts cover) are greyed out.
    const selectedShifts = shifts.filter(
      (s) => !!s.id && selectedShiftIds.includes(s.id),
    );

    const map = computeReservabilityMap(
      selectedShifts,
      tables,
      globalStart,
      globalClose,
    );

    // Overlay per-day blockers. A reservation with `blocked: true` is not a
    // real booking — it's a manual "this table is unavailable during this
    // window" flag. Fold its covered blocks into the not-reservable set for
    // each affected table so the grid renders them greyed out.
    for (const res of reservations) {
      if (!res.blocked) continue;
      if (
        typeof res.startTimeInBlocks !== 'number' ||
        typeof res.endTimeInBlocks !== 'number'
      ) {
        continue;
      }
      // Full-day blocks read back as 96..208 (after-midnight rule in
      // useReservations) — grey the whole visible day, not just 24:00+.
      let start = res.startTimeInBlocks;
      let end =
        res.endTimeInBlocks > res.startTimeInBlocks
          ? res.endTimeInBlocks
          : res.endTimeInBlocks + 96;
      if (isFullDayTableBlock(res)) {
        start = globalStart ?? 0;
        end = globalClose ?? 0;
      }

      for (const tableName of res.tables ?? []) {
        if (!map[tableName]) map[tableName] = new Set<number>();
        for (let b = start; b < end; b++) {
          if (b >= (globalStart ?? b) && b < (globalClose ?? b + 1)) {
            map[tableName].add(b);
          }
        }
      }
    }

    return map;
  }, [shifts, tables, timeTables, selectedShiftIds, reservations]);

  // Zoom state — pixel width of a single 15-min block. Drives the `<td>`
  // min-width via a CSS variable and the card-position math through a prop.
  // Bounds match the icon-button limits in the timeline.
  const MIN_BLOCK_WIDTH = 30;
  const MAX_BLOCK_WIDTH = 120;
  const ZOOM_STEP = 15;
  const [blockWidth, setBlockWidth] = useLocalStorageState<number>(
    'timeTable.blockWidth',
    60
  );

  // When the block width changes the horizontal scroll no longer points at
  // the same time. Before changing blockWidth we capture the number of
  // blocks currently centered in the viewport; a useLayoutEffect restores
  // the scroll position so that same time sits at the centre after the
  // layout reflows.
  const pendingZoomCenterRef = useRef<number | null>(null);

  const zoomBy = useCallback(
    (delta: number) => {
      const scrollContainer = document.getElementById('timeTableCard');
      if (scrollContainer) {
        const LABEL_OFFSET = 88;
        const visibleTimeWidth = Math.max(
          scrollContainer.clientWidth - LABEL_OFFSET,
          0
        );
        const centerScrollPx =
          scrollContainer.scrollLeft + visibleTimeWidth / 2;
        pendingZoomCenterRef.current =
          centerScrollPx / Math.max(blockWidth || 60, 1);
      }
      setBlockWidth((prev) =>
        Math.max(
          MIN_BLOCK_WIDTH,
          Math.min(MAX_BLOCK_WIDTH, (prev || 60) + delta)
        )
      );
    },
    [blockWidth, setBlockWidth]
  );

  // The "zoom in" icon collapses blocks so more of the day fits on screen;
  // "zoom out" expands them for a more detailed view of fewer hours. This
  // inverts the natural pixel-size→magnification mapping on purpose.
  const onZoomIn = useCallback(() => zoomBy(-ZOOM_STEP), [zoomBy]);
  const onZoomOut = useCallback(() => zoomBy(+ZOOM_STEP), [zoomBy]);

  useLayoutEffect(() => {
    if (pendingZoomCenterRef.current == null) return;
    const scrollContainer = document.getElementById('timeTableCard');
    if (!scrollContainer) {
      pendingZoomCenterRef.current = null;
      return;
    }
    const LABEL_OFFSET = 88;
    const visibleTimeWidth = Math.max(
      scrollContainer.clientWidth - LABEL_OFFSET,
      0
    );
    const targetScrollLeft =
      pendingZoomCenterRef.current * blockWidth - visibleTimeWidth / 2;
    scrollContainer.scrollLeft = Math.max(0, targetScrollLeft);
    pendingZoomCenterRef.current = null;
  }, [blockWidth]);

  // Track the horizontal viewport of the Card body (the element with
  // overflow: scroll) and convert it into the block range currently
  // visible in the timetable grid. We use constants that match the table
  // render: 88px sticky-th label column, 60px per 15-min block.
  const [visibleBlocks, setVisibleBlocks] = useState<{
    start: number;
    close: number;
  } | null>(null);

  const globalRangeStart = timeTables[0]?.start ?? null;
  const globalRangeClose = timeTables[0]?.close ?? null;

  useEffect(() => {
    if (globalRangeStart == null || globalRangeClose == null) {
      setVisibleBlocks(null);
      return;
    }

    const scrollContainer = document.getElementById('timeTableCard');
    if (!scrollContainer) {
      setVisibleBlocks({ start: globalRangeStart, close: globalRangeClose });
      return;
    }

    const LABEL_OFFSET = 88;

    const update = () => {
      const scrollLeft = scrollContainer.scrollLeft;
      const width = scrollContainer.clientWidth;
      // The sticky th column covers the first LABEL_OFFSET px of every
      // visible frame, so the time axis actually visible to the operator
      // runs from (scrollLeft) through (scrollLeft + width - LABEL_OFFSET)
      // in time-axis pixels (i.e. after subtracting the label column).
      const startBlock = scrollLeft / blockWidth + globalRangeStart;
      const closeBlock =
        (scrollLeft + Math.max(width - LABEL_OFFSET, 0)) / blockWidth +
        globalRangeStart;

      setVisibleBlocks({
        start: Math.max(globalRangeStart, startBlock),
        close: Math.min(globalRangeClose, closeBlock),
      });
    };

    update();
    scrollContainer.addEventListener('scroll', update, { passive: true });

    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(update);
      resizeObserver.observe(scrollContainer);
    } else {
      window.addEventListener('resize', update);
    }

    return () => {
      scrollContainer.removeEventListener('scroll', update);
      if (resizeObserver) resizeObserver.disconnect();
      else window.removeEventListener('resize', update);
    };
  }, [globalRangeStart, globalRangeClose, blockWidth]);

  const [skipRemoveConfirm, setSkipRemoveConfirm] =
    useLocalStorageState<boolean>('timeTable.skipRemoveTableConfirm', false);
  const [pendingRemove, setPendingRemove] = useState<null | {
    resId: string;
    tables: string[];
    space: string;
    thisTable: string;
  }>(null);
  const [dontAskChecked, setDontAskChecked] = useState(false);

  const [collapsedSpaces, setCollapsedSpaces] = useLocalStorageState<{
    [spaceId: string]: boolean;
  }>('timeTable.collapsedSpaces', {});

  const onToggleCollapse = useCallback(
    (spaceId: string) => {
      setCollapsedSpaces((prev) => ({
        ...prev,
        [spaceId]: !prev[spaceId],
      }));
    },
    [setCollapsedSpaces],
  );

  // ---- Lock button: toggle space closed/open via the same endpoint
  // SpaceSwitch uses. We go through usePinAuth so the manager PIN is
  // required before flipping the state.
  const { restaurantId } = useContext(RestaurantContext);
  const { uid, DateNow } = useContext(AuthContext);
  const { floorPlanProps, date, TablePlanAssistant } =
    useContext(ReservationContext);

  // The restaurant and day on screen, read after closeSpace returns: a
  // response for one the user already left must not replace its shifts.
  const screenKeyRef = useRef('');
  screenKeyRef.current = `${restaurantId}|${date}`;
  const assistantActive = TablePlanAssistant.active;
  const { authenticate } = usePinAuth();
  const toast = useToast();
  const [loadingSpaceId, setLoadingSpaceId] = useState<string | null>(null);
  // One closeSpace at a time: every response carries the whole day, so an
  // older one arriving last would undo the newer change on screen.
  const spaceWriting = useRef(false);

  const toggleSpaceClosed = useCallback(
    async (spaceId: string, grant?: SecurityGrant) => {
      if (assistantActive) return;

      const openShifts = shifts.filter((s) => s.active && !s.commentOnly);

      // Shifts that close the space for reasons the lock button cannot
      // flip — i.e. the whole shift is closed, the shift excludes this
      // space via matchBoth, or the shift blocks all reservations.
      const whereSpaceIsClosedByRule = openShifts.filter(
        (s) =>
          s.closed ||
          (s.spaces?.length && !s.spaces.includes(spaceId) && s.matchBoth) ||
          !!s.overwrite?.blockReservations,
      );

      if (
        openShifts.length === 0 ||
        whereSpaceIsClosedByRule.length === openShifts.length
      ) {
        toast('Bereich ist durch andere Regeln geschlossen.', Severity.ERROR);
        return;
      }

      const currentState = getSpaceClosedState(shifts, spaceId);
      if (!restaurantId || !date || spaceWriting.current) return;
      spaceWriting.current = true;

      try {
        setLoadingSpaceId(spaceId);
        const closed = currentState !== 2;
        const { data } = await trackChange(
          grant,
          closed ? 'space.block' : 'space.unblock',
          server.post<{ shifts: ReservationShift[] }>(
            `/v03/shifts/${restaurantId}/${date}/closeSpace`,
            { spaceId, closed },
          ),
          {
            targets: [{ type: 'space', id: spaceId }],
            changes: [
              { field: 'blocked', before: !closed, after: closed },
              { field: 'date', before: null, after: date },
            ],
          },
        );
        if (screenKeyRef.current === `${restaurantId}|${date}`)
          floorPlanProps.setShifts(data.shifts);
      } catch (error) {
        toast('Bereich konnte nicht geändert werden.', Severity.ERROR);
      } finally {
        spaceWriting.current = false;
        setLoadingSpaceId(null);
      }
    },
    [assistantActive, shifts, restaurantId, date, floorPlanProps, toast],
  );

  const onToggleSpaceClosed = useCallback(
    (spaceId: string) => {
      if (assistantActive) return;

      authenticate(
        'reservations.spaceBlocked',
        (_, grant) => toggleSpaceClosed(spaceId, grant),
        undefined,
        {
          action: getSpaceClosedState(shifts, spaceId) !== 2 ? 'space.block' : 'space.unblock',
          targets: [{ type: 'space', id: spaceId }],
        },
      );
    },
    [assistantActive, authenticate, toggleSpaceClosed, shifts],
  );

  // ---- Block/Unblock a single table for the whole day. Per-shift blocking
  // lives on the table sidebar, so this lock always means the same thing.
  const blockWriting = useRef(false);

  // A block only counts as "table blocked" while it overlaps the shifts the
  // user currently has selected — a table blocked for the evening must not
  // look blocked in the morning.
  const blockedTableNames = useMemo(() => {
    const set = new Set<string>();
    const [start, close] = selectedShiftRange;
    if (start === null || close === null) return set;

    for (const r of reservations) {
      if (!r.blocked || !tableBlockCoversWindow(r, start, close)) continue;
      for (const t of r.tables ?? []) set.add(t);
    }
    return set;
  }, [reservations, selectedShiftRange]);

  const toggleTableBlocked = useCallback(
    async (tableName: string, grant?: SecurityGrant) => {
      if (assistantActive) return;
      const table = tables.find((t) => t.name === tableName);
      if (!table || !restaurantId || !date) return;

      // A second click before the snapshot comes back would still see the old
      // state and write a second block document.
      if (blockWriting.current) return;
      blockWriting.current = true;

      const blocked = blockedTableNames.has(table.name);
      const [start, close] = selectedShiftRange;

      try {
        await trackChange(
          grant,
          blocked ? 'table.unblock' : 'table.block',
          toggleFullDayTableBlock({
            blocked,
            // Unblocking lifts what the selected shifts show, a block on another
            // shift of the day stays. Blocking replaces every block anyway.
            blocks: reservations.filter(
              (r) =>
                r.blocked &&
                r.tables?.includes(table.name) &&
                (!blocked ||
                  (start !== null &&
                    close !== null &&
                    tableBlockCoversWindow(r, start, close))),
            ),
            restaurantId,
            date,
            table,
            uid: uid || '',
            now: DateNow(),
          }),
          {
            targets: [{ type: 'table', id: table.name }],
            changes: [
              { field: 'blocked', before: blocked, after: !blocked },
              { field: 'date', before: null, after: date },
            ],
          },
        );
      } catch (e) {
        console.error('block toggle', e);
        toast('Sperre konnte nicht gespeichert werden.', Severity.ERROR);
      } finally {
        blockWriting.current = false;
      }
    },
    [
      assistantActive,
      tables,
      blockedTableNames,
      selectedShiftRange,
      reservations,
      restaurantId,
      date,
      uid,
      DateNow,
      toast,
    ],
  );

  const onToggleTableBlocked = useCallback(
    (tableName: string) => {
      if (assistantActive) return;

      authenticate(
        'reservations.tableBlocked',
        (_, grant) => toggleTableBlocked(tableName, grant),
        undefined,
        {
          action: blockedTableNames.has(tableName) ? 'table.unblock' : 'table.block',
          targets: [{ type: 'table', id: tableName }],
        },
      );
    },
    [assistantActive, authenticate, toggleTableBlocked, blockedTableNames],
  );

  const onRequestRemoveTable = useCallback(
    (resId: string, newTables: string[], space: string, thisTable: string) => {
      if (skipRemoveConfirm) {
        handleReservationAction(resId, ReservationActionTypes.CHANGE_TABLE, {
          tables: newTables,
          space,
        });
      } else {
        setPendingRemove({ resId, tables: newTables, space, thisTable });
        setDontAskChecked(false);
      }
    },
    [skipRemoveConfirm, handleReservationAction],
  );

  return (
    <>
      <Card
        title="Time Table"
        titleTranslation="reservations"
        headerPadding="sm"
        className="timeTable"
        bodyProps={{
          padding: 0,
          id: 'timeTableCard',
          style: {
            maxHeight: overlayOn
              ? 'calc(100vh - 190px)'
              : 'calc(100vh - 130px)',
            overflow: 'scroll',
            padding: '16px 0px',
            // Drives `td { min-width: var(--block-width) }` in the grid
            // and cascades down to every consumer inside the Card.
            ['--block-width' as any]: `${blockWidth}px`,
          },
        }}
      >
        <ShiftTimeline
          shifts={shifts}
          selectedShiftIds={selectedShiftIds}
          onToggle={onToggleShift}
          onSelectAll={onSelectAllShifts}
          visibleStartBlock={visibleBlocks?.start ?? null}
          visibleCloseBlock={visibleBlocks?.close ?? null}
          onZoomIn={onZoomIn}
          onZoomOut={onZoomOut}
          canZoomIn={blockWidth > MIN_BLOCK_WIDTH}
          canZoomOut={blockWidth < MAX_BLOCK_WIDTH}
        />
        <div className="timeTable-inner">
          <div className="track" id="trackRect" ref={measuredRef}>
            {timeTables.map((timeTable, index) => (
              <Table
                key={timeTable.id}
                index={index}
                data={timeTable}
                activeReservation={activeReservation}
                activeTable={activeTable}
                onTableClick={handleTableClick}
                trackRect={trackRect}
                occassions={occassions}
                handleReservationAction={handleReservationAction}
                onReservationClick={handleReservationClick}
                currentTime={currentTime}
                reservabilityMap={reservabilityMap}
                allTables={tables}
                onRequestRemoveTable={onRequestRemoveTable}
                collapsed={!!collapsedSpaces[timeTable.id]}
                onToggleCollapse={onToggleCollapse}
                spaceLoading={loadingSpaceId === timeTable.id}
                onToggleSpaceClosed={onToggleSpaceClosed}
                onToggleTableBlocked={onToggleTableBlocked}
                blockedTableNames={blockedTableNames}
                directControlsDisabled={assistantActive || loadingSpaceId !== null}
                blockWidth={blockWidth}
              />
            ))}
          </div>
        </div>
      </Card>
      <Modal
        open={!!pendingRemove}
        onClose={() => setPendingRemove(null)}
        severity="warning"
        title="Remove table from reservation"
        maxWidth="xs"
        actions={[
          {
            id: 'cancel',
            label: 'Cancel',
            onClick: () => setPendingRemove(null),
          },
          {
            id: 'remove',
            label: 'Remove',
            variant: 'primary',
            onClick: () => {
              if (!pendingRemove) return;
              if (dontAskChecked) setSkipRemoveConfirm(true);
              handleReservationAction(
                pendingRemove.resId,
                ReservationActionTypes.CHANGE_TABLE,
                {
                  tables: pendingRemove.tables,
                  space: pendingRemove.space,
                },
              );
              setPendingRemove(null);
            },
          },
        ]}
      >
        <p style={{ marginTop: 0 }}>
          {pendingRemove
            ? `Remove table ${pendingRemove.thisTable} from this reservation?`
            : ''}
        </p>
        <Checkbox
          label="Don't ask me again"
          checked={dontAskChecked}
          onChange={(_, checked) => setDontAskChecked(checked)}
        />
      </Modal>
    </>
  );
};

export default TimeTable;
